import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Bills } from "@/lib/api";
import { fmtDate, fmtDateTime, fmtMoney } from "@/lib/bill-utils";
import { Printer } from "lucide-react";

const Row = ({ label, value, bold }) => (
  <div className="flex items-baseline justify-between py-2 border-b border-neutral-200">
    <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 font-bold">
      {label}
    </div>
    <div
      className={`${bold ? "font-display font-extrabold text-lg" : "text-sm"} mono`}
    >
      {value}
    </div>
  </div>
);

const Receipt = () => {
  const { id } = useParams();
  const [bill, setBill] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const b = await Bills.get(id);
        setBill(b);
        setTimeout(() => window.print(), 500);
      } catch {
        // noop
      }
    })();
  }, [id]);

  if (!bill)
    return (
      <div className="p-10 text-sm text-neutral-500">Loading receipt…</div>
    );

  const lastPayment = bill.payments?.[bill.payments.length - 1];
  const fullyPaid = bill.status === "paid";

  const headline = fullyPaid
    ? "PAID IN FULL"
    : bill.total_paid > 0
      ? "PAYMENT RECORD"
      : "BILL RECORD";

  const statusLabel = (() => {
    switch (bill.status) {
      case "paid":
        return "FULLY PAID";
      case "part_paid":
        return "PARTIALLY PAID";
      case "part_paid_overdue":
        return "PARTIALLY PAID • OVERDUE";
      case "overdue":
        return "UNPAID • OVERDUE";
      case "due_soon":
        return "UNPAID • DUE SOON";
      default:
        return "UNPAID";
    }
  })();

  return (
    <div className="min-h-screen bg-white p-6 md:p-10">
      <div className="max-w-2xl mx-auto">
        <div className="no-print mb-6 flex justify-end">
          <button
            data-testid="print-btn"
            onClick={() => window.print()}
            className="border-2 border-black bg-black text-white font-bold uppercase tracking-widest text-xs px-5 py-3 hover:bg-white hover:text-black transition-colors inline-flex items-center gap-2"
          >
            <Printer className="h-4 w-4" /> Print
          </button>
        </div>

        <div
          data-testid="receipt"
          className="border-2 border-black p-8 md:p-10 bg-white"
        >
          <div className="flex items-start justify-between mb-10">
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-neutral-500 font-bold">
                Receipt
              </div>
              <div className="font-display font-black text-3xl tracking-tight mt-1">
                {headline}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[11px] uppercase tracking-[0.25em] text-neutral-500 font-bold">
                Issued
              </div>
              <div className="text-sm mono mt-1">
                {fmtDateTime(new Date().toISOString())}
              </div>
            </div>
          </div>

          <div className="mb-6">
            <div className="swiss-label">Vendor / Company</div>
            <div className="font-display font-extrabold text-2xl mt-1">
              {bill.company}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6 mb-6">
            <div>
              <div className="swiss-label">Invoice Number</div>
              <div className="hash font-bold text-base mt-1">
                {bill.invoice_number}
              </div>
            </div>
            <div>
              <div className="swiss-label">Original Due Date</div>
              <div className="text-base mt-1">{fmtDate(bill.due_date)}</div>
            </div>
          </div>

          <div className="space-y-0 mb-6">
            <Row label="Amount Owed" value={fmtMoney(bill.amount_due)} />
            <Row label="Amount Paid" value={fmtMoney(bill.total_paid)} bold />
            <Row
              label="Remaining Balance"
              value={fmtMoney(bill.remaining_balance)}
              bold
            />
            <Row
              label="Payment Date"
              value={lastPayment ? fmtDateTime(lastPayment.paid_at) : "—"}
            />
            <Row label="Status" value={statusLabel} />
          </div>

          {bill.notes && (
            <div className="mb-6">
              <div className="swiss-label mb-1">Notes</div>
              <div className="text-sm whitespace-pre-wrap">{bill.notes}</div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-6 mb-6 text-sm">
            <div>
              <div className="swiss-label">Emailed</div>
              <div className="mt-1">
                {bill.emailed ? fmtDateTime(bill.emailed_at) : "No"}
              </div>
            </div>
            <div>
              <div className="swiss-label">Texted</div>
              <div className="mt-1">
                {bill.texted ? fmtDateTime(bill.texted_at) : "No"}
              </div>
            </div>
          </div>

          {bill.payments && bill.payments.length > 0 && (
            <div className="mb-4">
              <div className="swiss-label mb-2">Payment History</div>
              <ul className="text-sm">
                {bill.payments.map((p, i) => (
                  <li
                    key={p.id}
                    className="flex justify-between py-1 border-b border-neutral-100 last:border-0"
                  >
                    <span>
                      {i + 1}. {fmtDateTime(p.paid_at)}
                      {p.method ? ` • ${p.method}` : ""}
                    </span>
                    <span className="mono font-bold">{fmtMoney(p.amount)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-10 pt-6 border-t-2 border-black text-[11px] uppercase tracking-[0.25em] text-neutral-500 text-center">
            Thank you — Billy Bill Reminders
          </div>
        </div>
      </div>
    </div>
  );
};

export default Receipt;
